import React, { useState, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import Navbar from "../components/layout/navbar/Navbar";
import Footer from "../components/layout/footer/Footer";
import AnnouncementBanner from "../components/common/AnnouncementBanner/AnnouncementBanner";
import { DEFAULT_STORE_ID } from "../firebase/firebase";

const MainLayout = () => {
  const [bannerHeight, setBannerHeight] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const bannerRef = useRef(null);
  const navbarRef = useRef(null);
  const layoutRef = useRef(null); // 1. NEW REF FOR THE SCROLL CONTAINER

  // Handle scroll behavior (Attached to the DIV, not Window)
  useEffect(() => {
    const scrollContainer = layoutRef.current; // Get the div element

    const handleScroll = () => {
      if (!scrollContainer) return;

      // 2. READ SCROLL TOP FROM THE DIV, NOT WINDOW
      const scrollTop = scrollContainer.scrollTop;

      // Debug to prove it works now
      // console.log("Div Scroll Top:", scrollTop);

      const shouldHideBanner = scrollTop > 32;
      setIsScrolled(shouldHideBanner);

      // Update banner visibility - don't hide bottom banners
      if (bannerRef.current) {
        const isBottomBanner =
          bannerRef.current.classList.contains("bottom-banner");
        if (shouldHideBanner && !isBottomBanner) {
          bannerRef.current.classList.add("hidden");
        } else {
          bannerRef.current.classList.remove("hidden");
        }
      }
    };

    if (scrollContainer) {
      // 3. ATTACH LISTENER TO THE DIV
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []); // Run once on mount

  // ... (Your existing banner height code remains exactly the same) ...
  useEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current && DEFAULT_STORE_ID) {
        const height = bannerRef.current.offsetHeight;
        setBannerHeight(height);
        // Only set navbar top offset if banner is at top (not bottom)
        if (!bannerRef.current.classList.contains("bottom-banner")) {
          document.documentElement.style.setProperty(
            "--navbar-top",
            `${height}px`
          );
        } else {
          document.documentElement.style.setProperty("--navbar-top", "0px");
        }
        document.documentElement.style.setProperty(
          "--announcement-banner-height",
          `${height}px`
        );
      } else {
        setBannerHeight(0);
        document.documentElement.style.setProperty(
          "--announcement-banner-height",
          "0px"
        );
        document.documentElement.style.setProperty("--navbar-top", "0px");
      }
    };
    const timeoutId = setTimeout(updateBannerHeight, 100);
    window.addEventListener("resize", updateBannerHeight);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateBannerHeight);
    };
  }, [DEFAULT_STORE_ID]);

  // Observer (Keep as is)
  useEffect(() => {
    if (bannerRef.current) {
      const observer = new ResizeObserver(() => {
        if (bannerRef.current && DEFAULT_STORE_ID) {
          const height = bannerRef.current.offsetHeight;
          setBannerHeight(height);
          // Only set navbar top offset if banner is at top (not bottom)
          if (!bannerRef.current.classList.contains("bottom-banner")) {
            document.documentElement.style.setProperty(
              "--navbar-top",
              `${height}px`
            );
          } else {
            document.documentElement.style.setProperty("--navbar-top", "0px");
          }
          document.documentElement.style.setProperty(
            "--announcement-banner-height",
            `${height}px`
          );
        }
      });
      observer.observe(bannerRef.current);
      return () => observer.disconnect();
    }
  }, [DEFAULT_STORE_ID]);

  return (
    <div
      ref={layoutRef} // 4. ATTACH THE REF HERE
      className="main-layout"
      style={{
        // 5. FORCE THIS DIV TO BE THE SCROLL CONTAINER
        height: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Banner Section - At top */}
      {DEFAULT_STORE_ID && (
        <div ref={bannerRef} className="banner-slide-wrapper top-banner">
          <AnnouncementBanner storeId={DEFAULT_STORE_ID} position="top" />
        </div>
      )}

      {/* Navbar with Prop */}
      <Navbar ref={navbarRef} isScrolled={isScrolled} />

      <main
        style={{
          flex: "1",
          marginTop: "0px",
          marginBottom: "0px",
          transition: "margin-top 0.3s ease-in-out",
        }}
      >
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default MainLayout;
